// WARNING: This multi-select implementation is not ideal from a UI or
// accessibility perspective, but it is good enough to ship. Ideally this would
// be implemented as an ARIA listbox, but we're just going with a combination of
// existing Zinnia components for now in order to speed up development. If/when
// we improve this, we should see if we can integrate our new component upstream
// into Zinnia, since other teams are solving similar problems.
//
// Sage Fennel (2023-02-06)
// #team-partner-sharing

/** @jsx jsx */
import { css, jsx, SerializedStyles } from '@emotion/core';
import {
  Colors,
  FloatingBox,
  Icon,
  LabeledCheckbox,
  TooltipWrapper,
  Typography,
} from '@zapier/design-system';
import { ComponentProps, ReactNode, useRef, useState } from 'react';

const styleContainer = css`
  display: flex;
  flex-direction: column;
  user-select: none;
  width: 100%;
  min-width: 0;
`;

const styleButton = css`
  all: unset;
  box-sizing: border-box;
  ${Typography.Body2}
  background: ${Colors.PrimeWhite};
  border: 1px solid ${Colors.GrayWarm6};
  border-radius: 3px;
  padding-left: 15px;
  padding-right: 5px;
  height: 42px;
  text-align: left;
  /* OK, so interactive elements shouldn't get "cursor: pointer" unless they're
  "<a>" tags or something simulating on "<a>" tag (which is usually a bad idea)
  BUT everything in Zinnia uses the "pointer" (👆) cursor, so I'm copying it
  here out of consistency. */
  cursor: pointer;

  &:hover, &:focus {
    border-color: ${Colors.UiPrimaryStrongest};
  }

  &:focus {
    outline: 2px solid transparent;
    box-shadow: 0 0 0 1px ${Colors.UiPrimaryStrongest};
  }

  & > div {
    display: flex;
    align-items: center;
    justify-content: start;
    gap: 10px;
  }

  & > div > div:first-of-type {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: auto;
  }
`;

const styleMenuContainer = css`
  position: relative;
  width: 100%;

  &:hidden {
    display: none;
  }
`;

const styleMenuItemsContainer = css`
  padding: 10px;

  [class*='TooltipWrapper__content'] {
    // This will be fixed on https://zapierorg.atlassian.net/browse/DS-1075
    z-index: 3 !important;

    & > span {
      max-width: unset !important;
    }
  }
`;

type MultiSelectItem = {
  value: string;
  label: string;
};

type Props = {
  children: ReactNode;
  items: MultiSelectItem[];
  selectedItems: MultiSelectItem[];
  onChange: (selectedItems: MultiSelectItem[]) => void;
  position?: ComponentProps<typeof FloatingBox>['position'];
  ariaLabel?: string;
  maxSelectedItems?: number;
  extraStyleMenuItemsContainer?: SerializedStyles;
  disabledItemTooltip?: string;
  emptyMessage?: string;
};

export default function MultiSelect({
  ariaLabel,
  children,
  items,
  onChange,
  position = 'south',
  selectedItems,
  maxSelectedItems,
  extraStyleMenuItemsContainer,
  disabledItemTooltip,
  emptyMessage,
}: Props) {
  const containerRef = useRef<HTMLDivElement>();
  const buttonRef = useRef<HTMLButtonElement>();
  const [isOpen, setIsOpen] = useState(false);

  const selectedItemsValues = new Set(selectedItems.map(item => item.value));

  return (
    // I'm relying on the keydown event bubbling up to close the menu... this is
    // kind of a false positive message here...
    //
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      css={styleContainer}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          setIsOpen(false);
          if (buttonRef.current) {
            buttonRef.current.focus();
          }
        }
      }}
      ref={containerRef}
    >
      <button
        aria-label={ariaLabel}
        css={styleButton}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        ref={buttonRef}
      >
        <div
        // Some browsers don't support `<button>` tags being flex containers
        // very well. And you need to wrap the children in an element so they
        // can be given `flex: auto;` for the layout to work correctly.
        >
          <div>{children}</div>
          <Icon name="arrowDropdown" size={24} />
        </div>
      </button>
      <div css={styleMenuContainer} hidden={!isOpen}>
        <FloatingBox
          align="stretch"
          onClickOutside={event => {
            if (!containerRef.current.contains(event.target as Node)) {
              setIsOpen(false);
            }
          }}
          position={position}
        >
          <div css={[styleMenuItemsContainer, extraStyleMenuItemsContainer]}>
            {items?.map((item, index) => {
              const checked = selectedItemsValues.has(item.value);
              const isDisabled = Boolean(
                selectedItemsValues.size === maxSelectedItems && !checked
              );

              return (
                <TooltipWrapper
                  content={isDisabled ? disabledItemTooltip : undefined}
                  isBlock={true}
                  key={item.value}
                  position={
                    items.length - 1 === index ? 'northwest' : 'southwest' // in case hovering last item so it does not add empty space
                  }
                >
                  {() => (
                    <LabeledCheckbox
                      checked={checked}
                      disabled={isDisabled}
                      key={item.value}
                      name={item.value}
                      onChange={() => {
                        const set = new Set(selectedItemsValues);
                        if (checked) {
                          set.delete(item.value);
                        } else {
                          set.add(item.value);
                        }
                        onChange(items.filter(x => set.has(x.value)));
                      }}
                      tabIndex={0}
                    >
                      {item.label}
                    </LabeledCheckbox>
                  )}
                </TooltipWrapper>
              );
            })}
            {(!items || !items.length) && !!emptyMessage && (
              <span>{emptyMessage}</span>
            )}
          </div>
        </FloatingBox>
      </div>
    </div>
  );
}
